import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Slide,
} from "@mui/material";
import Logo from "../src/Components/images/logo.png";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from '@mui/icons-material/Login';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from "framer-motion";

import { useNavigate, useLocation } from "react-router-dom";

const Homenavbar = ({ onRequestDemo, activeLink, setActiveLink }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const isManualClick = React.useRef(false);

  useEffect(() => {
    const handleScrollSpy = () => {
      // If we are currently scrolling from a manual click, ignore scroll spy
      if (isManualClick.current) return;

      if (location.pathname !== "/" && location.pathname !== "/home") {
        if (setActiveLink) {
          if (location.pathname === "/about-us") {
            setActiveLink("About Us");
          } else {
            setActiveLink("");
          }
        }
        return;
      }

      const sections = [
        { name: "Home", id: "home-section" },
        { name: "For Recruiters", id: "recruiters-section" },
        { name: "For Jobseekers", id: "jobseekers-section" },
        // { name: "Pricing", id: "pricing-section" },
        { name: "How it Works", id: "how-it-works" },
      ];

      // If at top, force Home
      if (window.scrollY < 100) {
        if (setActiveLink) setActiveLink("Home");
        return;
      }

      let potentialMatch = null;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Wider trigger for scroll spy
          if (rect.top <= 300 && rect.bottom >= 150) {
            if (section.name === activeLink) {
              potentialMatch = section.name;
              break;
            }
            if (!potentialMatch) potentialMatch = section.name;
          }
        }
      }

      if (potentialMatch && potentialMatch !== activeLink) {
        if (setActiveLink) setActiveLink(potentialMatch);
      }
    };

    window.addEventListener("scroll", handleScrollSpy);
    handleScrollSpy();
    return () => window.removeEventListener("scroll", handleScrollSpy);
  }, [location.pathname, setActiveLink, activeLink]);

  const handleScroll = (id) => {
    if (location.pathname !== "/" && location.pathname !== "/home") {
      navigate("/");
      setTimeout(() => {
        const section = document.getElementById(id);
        if (section) {
          section.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    } else {
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const navLinks = [
    { name: "For Recruiters", type: "scroll", target: "recruiters-section" },
    { name: "For Jobseekers", type: "scroll", target: "jobseekers-section" },
    { name: "How it Works", type: "scroll", target: "how-it-works" },
    // { name: "Pricing", type: "scroll", target: "pricing-section" },
  ];

  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.8 }}
    >
      <Box
        sx={{
          padding: {
            lg: "12px 60px",
            md: "10px 40px",
            sm: "8px 20px",
            xs: "8px 15px",
          },
          width: "100%",
          backgroundColor: "#FFF",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1100,
          boxShadow: "0 2px 20px rgba(0,0,0,0.03)",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Logo Section */}
          <Box
            component="img"
            src={Logo}
            alt="Jobsnprofiles | Logo"
            sx={{
              width: { lg: "170px", md: "150px", sm: "110px", xs: "90px" },
              cursor: "pointer",
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.02)",
              },
            }}
            onClick={() => window.open("https://jobsnprofiles.com/", "_blank")}
          />

          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              gap: { lg: "35px", md: "28px", sm: "20px" },
              alignItems: "center"
            }}
          >
            {[{ name: "Home", type: "link", path: "/" }, ...navLinks].map((link) => (
              <Typography
                key={link.name}
                sx={{
                  color: activeLink === link.name ? "#FF6B00" : "#475569",
                  cursor: "pointer",
                  fontSize: { lg: "15px", md: "14px", sm: "13px" },
                  fontWeight: activeLink === link.name ? 600 : 500,
                  fontFamily: "'Poppins', sans-serif",
                  position: "relative",
                  padding: "4px 2px",
                  borderBottom: `2.5px solid ${activeLink === link.name ? "#FF6B00" : "transparent"}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: "#FF6B00",
                  }
                }}
                onClick={() => {
                  isManualClick.current = true;
                  setActiveLink(link.name);
                  if (link.type === "scroll") {
                    handleScroll(link.target);
                  } else {
                    navigate(link.path);
                  }
                  // Reset the manual click flag after smooth scroll is likely finished
                  setTimeout(() => {
                    isManualClick.current = false;
                  }, 1000);
                }}
              >
                {link.name}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              gap: "16px",
              alignItems: "center"
            }}
          >
            <Button
              variant="outlined"
              startIcon={<LoginIcon />}
              sx={{
                color: "#475569", // Dark Slate
                borderColor: "#CBD5E1", // Light Grey Border
                borderWidth: "1.5px", // Slightly thicker border
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "'Poppins', sans-serif",
                borderRadius: "8px",
                px: 2.5,
                py: 0.8,
                "&:hover": {
                  color: "#FF6B00", // Orange Text on Hover
                  borderColor: "#FF6B00", // Orange Border on Hover
                  backgroundColor: "rgba(255, 107, 0, 0.05)",
                  borderWidth: "1.5px",
                }
              }}
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: "linear-gradient(45deg, #FF6B00 30%, #FF9100 90%)",
                color: "white",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "'Poppins', sans-serif",
                px: 3,
                py: 1,
                boxShadow: "0 4px 14px 0 rgba(255, 107, 0, 0.39)",
                "&:hover": {
                  background: "linear-gradient(45deg, #E65100 30%, #FF6B00 90%)",
                  boxShadow: "0 6px 20px rgba(255, 107, 0, 0.23)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.3s ease"
              }}
              onClick={onRequestDemo}
            >
              Request a Demo
            </Button>
          </Box>

          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button onClick={toggleDrawer} sx={{ color: "#FF6B00" }}>
              <MenuIcon sx={{ fontSize: "28px" }} />
            </Button>
          </Box>

          {/* Drawer for Mobile */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer}
            sx={{
              "& .MuiDrawer-paper": {
                width: "280px",
                backgroundColor: "#ffffff",
                boxShadow: "-10px 0 30px rgba(0, 0, 0, 0.05)",
                padding: "20px",
              },
            }}
          >
            <Slide direction="left" in={drawerOpen} mountOnEnter unmountOnExit>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                  }}
                >
                  <Box
                    component="img"
                    src={Logo}
                    alt="Logo"
                    sx={{ width: "120px" }}
                  />
                  <Button onClick={toggleDrawer} sx={{ color: "#475569", minWidth: "auto" }}>
                    <CloseIcon />
                  </Button>
                </Box>
                <List sx={{ pt: 0 }}>
                  {[{ name: "Home", type: "link", path: "/" }, ...navLinks].map((link) => (
                    <ListItem
                      key={link.name}
                      button
                      onClick={() => {
                        setActiveLink(link.name);
                        if (link.type === "scroll") {
                          handleScroll(link.target);
                        } else {
                          navigate(link.path);
                        }
                        toggleDrawer();
                      }}
                      sx={{
                        borderRadius: "8px",
                        mb: 1,
                        borderLeft: `4px solid ${activeLink === link.name ? "#FF6B00" : "transparent"}`,
                        "&:hover": { backgroundColor: "rgba(255, 107, 0, 0.08)" },
                        backgroundColor: activeLink === link.name ? "rgba(255, 107, 0, 0.08)" : "transparent",
                      }}
                    >
                      <ListItemText
                        primary={link.name}
                        sx={{
                          color: activeLink === link.name ? "#FF6B00" : "#475569",
                          "& span": {
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: activeLink === link.name ? 600 : 500,
                            fontSize: "16px",
                          },
                        }}
                      />
                    </ListItem>
                  ))}

                  <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<LoginIcon />}
                      sx={{
                        color: "#475569",
                        borderColor: "#CBD5E1",
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: "'Poppins', sans-serif",
                        borderRadius: "8px",
                        py: 1.2,
                        "&:hover": { borderColor: "#FF6B00", color: "#FF6B00", backgroundColor: "rgba(255, 107, 0, 0.05)" }
                      }}
                      onClick={() => { navigate("/login"); toggleDrawer(); }}
                    >
                      Sign In
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: "linear-gradient(45deg, #FF6B00 30%, #FF9100 90%)",
                        color: "white",
                        textTransform: "none",
                        fontWeight: 600,
                        fontFamily: "'Poppins', sans-serif",
                        borderRadius: "8px",
                        py: 1.2,
                        boxShadow: "0 4px 14px 0 rgba(255, 107, 0, 0.39)",
                        "&:hover": {
                          background: "linear-gradient(45deg, #E65100 30%, #FF6B00 90%)"
                        }
                      }}
                      onClick={() => { onRequestDemo(); toggleDrawer(); }}
                    >
                      Request a Demo
                    </Button>
                  </Box>
                </List>
              </Box>
            </Slide>
          </Drawer>
        </Container>
      </Box>
    </motion.div>
  );
};

export default Homenavbar;
